/* @sravni/react-design-system version 0.2.65 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStateCallback, useStyles, findIndex, removeIndex, addItem, safeInvoke, isNull } from '@sravni/react-utils';
import '../../Space/index.js';
import AccordionContext from './AccordionContext.js';
import modules_127cd76a from '../styles/Accordion.module.less.js';
import '../types/Accordion.types.js';
import Space from '../../Space/src/Space.js';

/** Helper function normalizes indexes */
var normalizeIndexes = function (children, index) {
    if (Array.isArray(index)) {
        return index;
    }
    if (index === '*') {
        var index_1 = React.Children.map(children, function (_, i) { return i; }) || [];
        return index_1;
    }
    return [];
};
/**
 * @description A content area which can be collapsed and expanded
 *
 * @component
 * @example
 * ```jsx
 * <Accordion activeKeys={['credit']}>
 *   <Accordion.Item key="credit" title="О кредите">content</Accordion.Item>
 * </Accordion>
 * ```
 */
var Accordion = React.forwardRef(function (props, ref) {
    var _a;
    var index = props.index, defaultIndex = props.defaultIndex, separator = props.separator, background = props.background, padding = props.padding, scrollIntoView = props.scrollIntoView, children = props.children, className = props.className, onChange = props.onChange, restProps = __rest(props, ["index", "defaultIndex", "separator", "background", "padding", "scrollIntoView", "children", "className", "onChange"]);
    var _b = __read(useStateCallback(normalizeIndexes(children, index !== null && index !== void 0 ? index : defaultIndex)), 2), innerValue = _b[0], setInnerValue = _b[1];
    var cx = useStyles(modules_127cd76a);
    var classNames = cx(className, (_a = {},
        _a["separator-" + separator] = separator,
        _a["background-" + background] = background,
        _a));
    var handleCollapse = React.useCallback(function (event) {
        var value = Number(event.currentTarget.dataset.index);
        var pos = findIndex(innerValue, value);
        var indexes = pos >= 0 ? removeIndex(innerValue, pos) : addItem(innerValue, value);
        setInnerValue(indexes, function (val) {
            safeInvoke(onChange, val || []);
        });
    }, [innerValue, onChange]);
    var separatorSpace = separator === 'space' ? 16 : 0;
    var panels = React.useMemo(function () {
        return React.Children.map(children, function (child, index) {
            if (isNull(child))
                return;
            var childProps = __assign(__assign({}, child.props), { id: index, active: innerValue.indexOf(index) >= 0, onCollapse: handleCollapse });
            return React.cloneElement(child, childProps);
        });
    }, [innerValue, children, handleCollapse]);
    React.useEffect(function () {
        var indexes = normalizeIndexes(children, index !== null && index !== void 0 ? index : defaultIndex);
        setInnerValue(indexes);
    }, [children, index]);
    return (React.createElement(Space, __assign({ ref: ref, "data-qa": "Accordion", size: separatorSpace, direction: "vertical", className: classNames }, restProps),
        React.createElement(AccordionContext.Provider, { value: { scrollIntoView: scrollIntoView, padding: padding } }, panels)));
});
Accordion.defaultProps = {
    separator: 'space',
    scrollIntoView: true,
    padding: true,
};
Accordion.displayName = 'Accordion';

export default Accordion;
//# sourceMappingURL=Accordion.js.map
